import { Controller } from "@hotwired/stimulus"

// All editable state belongs to the URL. The JSON schema and Ruby ToolOutput
// define the same normalization and formatting rules for every tool.
export default class extends Controller {
  static targets = ["schema", "form", "preview", "htmlOutput", "classOutput", "link", "markdown", "presetField", "stopsList", "picker"]

  connect() {
    this.schema = JSON.parse(this.schemaTarget.textContent)
    this.values = { ...this.schema.values }
    this.selectedPreset = this.schema.preset
    this.stopTemplate = this.hasStopsListTarget ? this.stopsListTarget.firstElementChild?.cloneNode(true) : null
  }

  change(event) {
    const name = event.target.name
    if (!this.schema.params[name]) return
    this.values[name] = this.normalize(event.target.value, this.schema.params[name])
    this.refresh()
  }

  range(event) {
    const name = event.target.dataset.param
    this.values[name] = this.normalize(event.target.value, this.schema.params[name])
    this.refresh()
  }

  color(event) {
    const name = event.target.dataset.param
    const previous = this.values[name].toString()
    this.values[name] = event.target.value.slice(1).toUpperCase() + (previous.length === 8 ? previous.slice(6) : "")
    this.refresh()
  }

  toggle(event) {
    this.values[event.target.dataset.param] = event.target.checked ? "1" : "0"
    this.refresh()
  }

  preset(event) {
    event.preventDefault()
    this.selectedPreset = event.currentTarget.dataset.preset
    this.values = { ...this.schema.preset_values[this.selectedPreset] }
    this.refresh()
  }

  reset() {
    this.values = this.selectedPreset
      ? { ...this.schema.preset_values[this.selectedPreset] }
      : Object.fromEntries(Object.entries(this.schema.params).map(([name, config]) => [name, this.normalize(config.default, config)]))
    this.refresh()
  }

  randomize() {
    const colors = Array.from({ length: 3 }, () => Math.floor(Math.random() * 0x1000000).toString(16).padStart(6, "0").toUpperCase())
    if (this.schema.params.stops) this.values.stops = this.normalize(colors.join(","), this.schema.params.stops)
    if (this.schema.params.angle) this.values.angle = Math.floor(Math.random() * 361)
    this.refresh()
  }

  changeStop(event) {
    const row = event.target.closest("[data-stop-index]")
    const list = row.parentElement
    const name = list.dataset.param
    const stops = this.parseStops(this.values[name])
    const index = Number(row.dataset.stopIndex)
    if (event.target.dataset.stopField === "picker") {
      stops[index][0] = event.target.value.slice(1).toUpperCase() + (stops[index][0].length === 8 ? stops[index][0].slice(6) : "")
    } else if (event.target.dataset.stopField === "hex") {
      const color = event.target.value.replace(/^#/, "").toUpperCase()
      if (!/^(?:[0-9A-F]{6}|[0-9A-F]{8})$/.test(color)) return
      stops[index][0] = color
    } else {
      const position = Number(event.target.value)
      if (!Number.isFinite(position) || position < 0 || position > 100) return
      stops[index][1] = this.number(position)
    }
    this.setStops(name, stops)
  }

  addStop(event) {
    const name = event.currentTarget.dataset.param
    const stops = this.parseStops(this.values[name])
    if (stops.length >= 8) return
    stops.push(["FFFFFF", "100"])
    this.setStops(name, stops.map(([hex], index) => [hex, this.number(index / (stops.length - 1) * 100)]))
  }

  removeStop(event) {
    const row = event.currentTarget.closest("[data-stop-index]")
    const name = row.parentElement.dataset.param
    const stops = this.parseStops(this.values[name])
    if (stops.length <= 2) return
    stops.splice(Number(row.dataset.stopIndex), 1)
    this.setStops(name, stops)
  }

  moveStop(event) {
    const row = event.currentTarget.closest("[data-stop-index]")
    const name = row.parentElement.dataset.param
    const stops = this.parseStops(this.values[name])
    const index = Number(row.dataset.stopIndex)
    const next = index + Number(event.currentTarget.dataset.direction)
    if (next < 0 || next >= stops.length) return
    // Move the color and keep the positional slots in their visible order.
    ;[stops[index][0], stops[next][0]] = [stops[next][0], stops[index][0]]
    this.setStops(name, stops)
  }

  setStops(name, stops) {
    this.values[name] = stops.map(stop => stop.join("@")).join(",")
    this.refresh()
  }

  parseStops(value) {
    return value.split(",").map(stop => stop.split("@"))
  }

  openPicker(event) {
    this.paletteParam = event.currentTarget.dataset.param
    this.pickerTarget.hidden = false
  }

  closePicker() {
    this.pickerTarget.hidden = true
  }

  choosePalette(event) {
    const name = this.paletteParam || "stops"
    this.values[name] = this.normalize(event.currentTarget.dataset.colors, this.schema.params[name])
    this.refresh()
    this.closePicker()
  }

  normalize(value, config, fallback = true) {
    const raw = String(value)
    let result = null
    switch (config.type) {
      case "text":
        result = Array.from(raw).slice(0, config.max_length || 40).join("")
        break
      case "color":
        if (/^(?:[a-f0-9]{6}|[a-f0-9]{8})$/i.test(raw)) result = raw.toUpperCase()
        break
      case "select":
        if (config.options.includes(raw)) result = raw
        break
      case "toggle":
        if (["0", "1"].includes(raw)) result = raw
        break
      case "range": {
        const number = Number(raw)
        if (/^-?(?:[0-9]+(?:\.[0-9]+)?|\.[0-9]+)$/.test(raw) && number >= config.min && number <= config.max) {
          const steps = Math.round(Number(((number - config.min) / config.step).toFixed(10)))
          result = Number(Math.min(config.min + steps * config.step, config.max).toFixed(10))
        }
        break
      }
      case "stops": {
        const stops = raw.split(",")
        if (stops.length < 2 || stops.length > 8) break
        const normalized = stops.map((stop, index) => {
          const match = stop.trim().match(/^([a-f0-9]{6}(?:[a-f0-9]{2})?)(?:@([0-9]+(?:\.[0-9]+)?))?$/i)
          if (!match) return null
          const position = match[2] === undefined ? index / (stops.length - 1) * 100 : Number(match[2])
          if (position < 0 || position > 100) return null
          return `${match[1].toUpperCase()}@${this.number(position)}`
        })
        if (normalized.every(stop => stop !== null)) result = normalized.join(",")
        break
      }
    }
    return result === null && fallback ? this.normalize(config.default, config, false) : result
  }

  number(value) {
    return String(Math.round((Number(value) + Number.EPSILON) * 100) / 100)
  }

  escape(value) {
    return String(value).replace(/[&<>"']/g, character => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[character])
  }

  properties() {
    return Object.entries(this.schema.params).flatMap(([name, config]) => {
      if (!config.var) return []
      const value = this.values[name]
      let format = config.format
      if (format && typeof format === "object") {
        format = format[value]
        if (format === null || format === undefined) return []
      }
      let formatted
      if (config.type === "stops") {
        formatted = this.parseStops(value).map(([color, position]) => `#${color} ${position}%`).join(", ")
      } else if (format) {
        formatted = format.replace(/\{([a-z][a-z0-9_]*)\}/g, (_, key) => this.printValue(key === "value" ? value : this.values[key]))
      } else {
        formatted = this.printValue(value)
      }
      return [[config.var, formatted]]
    })
  }

  printValue(value) {
    return typeof value === "number" ? this.number(value) : String(value)
  }

  outputs() {
    const properties = this.properties()
    const inline = properties.map(([key, value]) => `${key}: ${value};`).join(" ")
    const snippet = this.schema.snippet_html.trim().replace(/\{\{([a-z][a-z0-9_]*)\}\}/g, (_, name) => this.escape(this.values[name] ?? ""))
    const template = document.createElement("template")
    template.innerHTML = snippet
    const root = template.content.firstElementChild
    const existing = (root.getAttribute("style") || "").trim()
    root.setAttribute("style", [existing ? existing.replace(/;?$/, ";") : "", inline].filter(Boolean).join(" "))
    const html = template.innerHTML
    const css = this.schema.style_css.trimEnd()
    return {
      html,
      htmlCss: `${html}\n\n<style>\n${css}\n</style>`,
      cssClass: `.my-${this.schema.slug} {\n${properties.map(([key, value]) => `  ${key}: ${value};`).join("\n")}\n}\n\n${css}`
    }
  }

  refresh() {
    if (this.values.motion && this.values.motion !== "none" && Number(this.values.spread) === 100) this.values.spread = 200
    // Turbo caches the rendered DOM when navigating away. Keep its schema in
    // sync so reconnecting the restored page cannot reset the other controls.
    this.schema.values = { ...this.values }
    this.schema.preset = this.selectedPreset
    this.schemaTarget.textContent = JSON.stringify(this.schema).replace(/</g, "\\u003c")
    this.syncControls()
    const output = this.outputs()
    this.previewTarget.querySelector(".ui-stage-content").innerHTML = output.html
    this.writeOutput(this.htmlOutputTarget, output.htmlCss)
    this.writeOutput(this.classOutputTarget, output.cssClass)
    const url = new URL(window.location.href)
    url.search = ""
    if (this.selectedPreset) url.searchParams.set("preset", this.selectedPreset)
    Object.entries(this.values).forEach(([name, value]) => url.searchParams.set(name, this.printValue(value)))
    history.replaceState(history.state, "", url)
    this.linkTarget.value = url.href
    url.pathname += ".md"
    this.markdownTarget.href = url.href
    this.element.querySelectorAll("[data-preset]").forEach(button => {
      if (button.dataset.preset === this.selectedPreset) button.setAttribute("aria-current", "true")
      else button.removeAttribute("aria-current")
    })
  }

  writeOutput(container, text) {
    const input = container.querySelector('[data-clipboard-target="input"]')
    if (["INPUT", "TEXTAREA"].includes(input.tagName)) input.value = text
    else input.textContent = text
  }

  syncControls() {
    this.presetFieldTarget.value = this.selectedPreset || ""
    Object.entries(this.schema.params).forEach(([name, config]) => {
      const value = this.values[name]
      this.formTarget.querySelectorAll(`[name="${name}"]`).forEach(input => {
        if (input.type === "radio") input.checked = input.value === String(value)
        else input.value = value
      })
      this.formTarget.querySelectorAll(`[data-param="${name}"]`).forEach(input => {
        if (input.type === "range") input.value = value
        if (input.type === "color") input.value = `#${value.slice(0, 6)}`
        if (input.type === "checkbox") input.checked = value === "1"
      })
      if (config.type === "stops") this.renderStops(name)
    })
  }

  renderStops(name) {
    const list = this.stopsListTargets.find(target => target.dataset.param === name)
    if (!list || !this.stopTemplate) return
    const active = document.activeElement
    const activeRow = active?.closest("[data-stop-index]")
    const focusIndex = activeRow?.parentElement === list ? activeRow.dataset.stopIndex : null
    const focusField = active?.dataset.stopField
    const stops = this.parseStops(this.values[name])
    const rows = stops.map(([color, position], index) => {
      const row = list.children[index] || this.stopTemplate.cloneNode(true)
      row.dataset.stopIndex = index
      row.querySelector('[data-stop-field="picker"]').value = `#${color.slice(0, 6)}`
      row.querySelector('[data-stop-field="hex"]').value = color
      row.querySelector('[data-stop-field="position"]').value = position
      row.querySelectorAll("[aria-label]").forEach(input => input.setAttribute("aria-label", input.getAttribute("aria-label").replace(/\d+/, String(index + 1))))
      row.querySelector('[data-direction="-1"]').disabled = index === 0
      row.querySelector('[data-direction="1"]').disabled = index === stops.length - 1
      row.querySelector('[data-action="ui-customizer#removeStop"]').disabled = stops.length <= 2
      return row
    })
    if (list.children.length !== rows.length) {
      list.replaceChildren(...rows)
      if (focusIndex !== null && focusField) list.querySelector(`[data-stop-index="${focusIndex}"] [data-stop-field="${focusField}"]`)?.focus()
    }
    this.element.querySelector(`[data-action="ui-customizer#addStop"][data-param="${name}"]`).disabled = stops.length >= 8
  }
}
